import { cssSpacing, typography as designTypography, borderRadius as designBorderRadius, spacing } from "./app/design-system";
import React from "react";
import ReactDOM from "react-dom/client";
// @dnd-kit ZUERST laden, bevor App/Router/PersonDetailPage – vermeidet TDZ "Cannot access 'Dn' before initialization"
import "@dnd-kit/core";
import "@dnd-kit/sortable";
import "@dnd-kit/utilities";
// Dayjs-Plugins vor allen anderen Modulen ausführen (siehe dayjs-init.ts).
import "./dayjs-init";
import { App } from "./app/App";
import { isBackendUnreachableApiError,isTenantImportCommitBlockedApiError,TENANT_IMPORT_COMMIT_BLOCKED_EVENT } from "./app/api/tenantImportCommitBlocked";
import { isAbortError, isFormValidationError } from "./app/utils/errorMessage";
import { postClientErrorOnce } from "./app/errors/clientErrorReporter";
import { installGlobalClientErrorHandlers } from "../../../../shared/frontend/feedback/globalClientErrorHandlers";
import { isIgnorableWindowError } from "./app/errors/ignorableBrowserErrors";
import { trStatic } from "./app/i18n/static";
import { requestManualAppReload } from "./app/system/appReloadCoordinator";
import { signalAppRuntimeUnavailable } from "../../../../shared/frontend/system/appRuntimeGate";
import { publishAppVersionMetadata } from "./app/version";
import { legacyAdministrationUrl } from "./app/utils/publicWidgetDocument";
import "./styles/global.css";
// Gemeinsame Oberfläche in fester Reihenfolge (ui/app.css); Werte aus dem gewählten Theme.
import "../../../../shared/frontend/ui/app.css";
import "./app/theme/styles/base.css";
import { ROUTE_CANCEL_BOOKING } from "./app/constants/appRoutes";

/**
 * Der UI-Router ist ein Hash-Router (createHashRouter). Einige extern geteilte Links
 * verwenden aber "schöne" Pfade wie /book/<code> oder /lead/<code> ohne Hash.
 *
 * Ohne diese Normalisierung würde die App auf / (Login/Dashboard) routen.
 * Wir mappen daher NUR diese Public-Pfade auf ihre Hash-Entsprechung.
 *
 * Wichtig: Diese Logik muss VOR dem Rendern laufen.
 */
function normalizePublicDeepLinksForHashRouter() {
  try {
    const { origin, pathname, search, hash } = window.location;
    if (hash && hash.startsWith("#/")) return;

    const isPublicDeepLink =
      (pathname.startsWith("/book/") && pathname.length > "/book/".length) ||
      (pathname.startsWith("/lead/") && pathname.length > "/lead/".length) ||
      (pathname.startsWith("/contract-widget/") && pathname.length > "/contract-widget/".length) ||
      pathname === ROUTE_CANCEL_BOOKING;

    if (!isPublicDeepLink) return;

    // Move query string into hash so the hash-router can see it.
    // Example: /lead/abc?x=1  ->  /lead/abc#/lead/abc?x=1
    const target = `${origin}${pathname}#${pathname}${search || ""}`;
    window.location.replace(target);
  } catch {
    // ignore
  }
}

publishAppVersionMetadata();
normalizePublicDeepLinksForHashRouter();

/**
 * Fachlich erwartete Zustände, die über window.onerror oder unhandledrejection ankommen, aber kein
 * Absturz sind: kein Fehler-Overlay und keine Meldung ans Backend.
 */
function handleExpectedWindowError(reason: unknown): boolean {
  // Import-/Sitzungswechsel beenden veraltete Leseabrufe absichtlich.
  if (isAbortError(reason)) return true;
  // Mandant gesperrt während Datenimport — nur /me nachziehen (siehe TenantImportBlockedListener).
  if (isTenantImportCommitBlockedApiError(reason)) {
    window.dispatchEvent(new CustomEvent(TENANT_IMPORT_COMMIT_BLOCKED_EVENT));
    return true;
  }
  // Browserhinweise ohne Anwendungsfehler (ResizeObserver, Passkey-Unterstützung).
  if (isIgnorableWindowError(reason instanceof Error ? reason.message : reason)) return true;
  // `validateFields()` lehnt mit einem Objekt ab. Ein vergessener Handler ist kein Absturz; das
  // Formular hat die betroffenen Felder bereits markiert.
  if (isFormValidationError(reason)) {
    if (import.meta.env.DEV) console.warn("[unhandledrejection] Formularprüfung ohne Handler", reason);
    return true;
  }
  // Erwartete transiente Wartung (Deploy/Restart/Migration: 502/503/504): das gemeinsame
  // Wartungs-Overlay übernimmt die Anzeige.
  if (isBackendUnreachableApiError(reason)) {
    signalAppRuntimeUnavailable();
    return true;
  }
  return false;
}

if (typeof window !== "undefined") {
  installGlobalClientErrorHandlers(postClientErrorOnce, { handleExpected: handleExpectedWindowError });
  // Ab hier übernehmen die Shared-Handler und GlobalErrorOverlay. Die Handler aus index.html sind nur
  // für frühe Modul-Load-Fehler gedacht und würden sonst zusätzlich ihr eigenes Overlay zeigen.
  window.onerror = null;
  window.onunhandledrejection = null;
}

/** Äußerer Fehlerfänger: kein Context, keine Hooks. Fängt alles, was aus der App entweicht (inkl. Fehler im inneren ErrorBoundary-Fallback). */
class OuterErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(thrown: unknown) {
    const error =
      thrown instanceof Error ? thrown : new Error(typeof thrown === "string" ? thrown : String(thrown));
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.45)",
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            padding: spacing.xl,
            boxSizing: "border-box"
          }}
        >
          <div
            style={{
              maxWidth: 480,
              padding: spacing.xxl,
              background: "#1f1f1f",
              color: "rgba(255,255,255,0.85)",
              borderRadius: designBorderRadius.lg,
              boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
              textAlign: "center"
            }}
          >
            <h2 style={{ margin: cssSpacing(0, 0, spacing.lg), fontSize: designTypography.fontSize.lg }}>{trStatic("An error occurred")}</h2>
            <p style={{ margin: cssSpacing(0, 0, spacing.xl), fontSize: designTypography.fontSize.base, opacity: 0.9 }}>
              {trStatic("Please reload the page. If the error occurs repeatedly, contact support.")}
            </p>
            <button
              type="button"
              onClick={() => requestManualAppReload()}
              style={{
                padding: cssSpacing(spacing.sm, spacing.contentGap),
                fontSize: designTypography.fontSize.base,
                cursor: "pointer",
                background: "#4096ff",
                color: "#fff",
                border: "none",
                borderRadius: designBorderRadius.md
              }}
            >
              {trStatic("Reload page")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const administrationUrl = legacyAdministrationUrl(window.location, window.self !== window.top);
if (administrationUrl) window.location.replace(administrationUrl);
else ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <OuterErrorBoundary>
      <App />
    </OuterErrorBoundary>
  </React.StrictMode>
);
